/**
 * Dependency-free slippy map: raster tiles, pan / zoom / pinch, markers, an
 * optional route line, and a Street / Satellite / Hybrid switcher.
 * Only tile images are fetched from the basemap host — no case data leaves CivilHound.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { basemapsFor, fitBounds, lngLatToWorld, MAP_LAYERS, TILE_SIZE, tileUrl, worldToLngLat, type MapLayerId } from '@civilos/domain';
import { api } from './api.ts';
import { CAR_CHOICES, CarShape } from './carIcons.tsx';
import { useAuth } from './auth.tsx';

/** House numbers from the county's address points appear from this zoom in. */
const HOUSE_NUMBER_ZOOM = 17;

/** A line or an area to draw on the map. */
export interface MapVector {
  points: { lat: number; lng: number }[];
  color: string;
  area?: boolean;
}

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  color: string;
  /** Short text inside the pin (e.g. stop number). */
  label?: string;
  title: string;
  selected?: boolean;
  /** Smaller round dot instead of a pin (e.g. attempt GPS points). */
  dot?: boolean;
  /** A spinning blue diamond right on the spot (the house where a paper is), under the pin. */
  diamond?: boolean;
}

interface Props {
  markers: MapMarker[];
  route?: { lat: number; lng: number }[];
  /** Your position; the patrol car points along `heading` (degrees, 0 = north) when it's known. */
  me?: { lat: number; lng: number; accuracy?: number; heading?: number | null } | null;
  height?: number | string;
  /** Changing this re-fits the view to the markers. */
  fitKey?: string;
  onMarkerClick?: (id: string) => void;
  onMapClick?: (p: { lat: number; lng: number }) => void;
  crosshair?: boolean;
  /** Draw mode: dragging traces a shape (instead of panning) and calls onDraw with its outline. */
  drawing?: boolean;
  onDraw?: (outline: { lat: number; lng: number }[]) => void;
  /** Shapes to show (e.g. areas already drawn). */
  shapes?: { lat: number; lng: number }[][];
  /** Map layers: lines and areas in a color (agency GIS layers). */
  vectors?: MapVector[];
  /** Changing this (to a new number) centers on `me`, zooms to street level and follows (e.g. when directions start). */
  followSignal?: number;
  ariaLabel?: string;
  children?: ReactNode;
}

const MIN_Z = 3;
const MAX_Z = 19;
const LAYER_KEY = 'civilos.mapLayer';
const FOLLOW_KEY = 'civilos.mapFollow';

const HEADING_KEY = 'civilos.mapHeadingUp';
/** Heading up is the default; a person can switch to north up (saved on this device). */
function loadHeadingUp(): boolean {
  try {
    return localStorage.getItem(HEADING_KEY) !== '0';
  } catch {
    return true;
  }
}

function loadFollowPref(): boolean {
  try {
    return localStorage.getItem(FOLLOW_KEY) === '1';
  } catch {
    return false;
  }
}

function loadLayer(): MapLayerId {
  try {
    const v = localStorage.getItem(LAYER_KEY);
    if (v === 'street' || v === 'satellite' || v === 'hybrid') return v;
  } catch {
    /* storage unavailable */
  }
  return 'street';
}

export function SlippyMap({ markers, route, me, height = 420, fitKey, onMarkerClick, onMapClick, crosshair, drawing, onDraw, shapes, vectors, followSignal, ariaLabel = 'Map', children }: Props) {
  const [trace, setTrace] = useState<{ x: number; y: number }[] | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [view, setView] = useState<{ lat: number; lng: number; zoom: number }>({ lat: 34.0, lng: -81.03, zoom: 8 });
  const [layerId, setLayerId] = useState<MapLayerId>(loadLayer);
  // Follow me: the map keeps your car in the middle as you move. Dragging the map stops it; the button turns it back on.
  const [follow, setFollow] = useState(false);
  const followRef = useRef(false);
  followRef.current = follow && !!me;
  // The person's choice (this device): start every map following their car.
  const [followPref, setFollowPref] = useState(loadFollowPref);
  const [headingUp, setHeadingUp] = useState(loadHeadingUp);
  const toggleHeadingUp = () => {
    const on = !headingUp;
    setHeadingUp(on);
    try {
      localStorage.setItem(HEADING_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
  };
  const autoFollowed = useRef(false);
  const [fitted, setFitted] = useState<string | null>(null);
  const auth = useAuth();
  // Your vehicle on the map (saved with your account); tap your car to change it.
  const [carIcon, setCarIcon] = useState<string | null>(auth?.me?.user.carIcon ?? null);
  const [carPicker, setCarPicker] = useState(false);
  const chooseCar = (id: string) => {
    setCarIcon(id);
    setCarPicker(false);
    api.post('/api/me/prefs', { carIcon: id }).then(() => auth?.refresh?.()).catch(() => undefined);
  };
  const maps = auth?.me?.agency.maps;
  // If an Esri layer won't load (bad key, or a key without that service — street and labels use Esri's static
  // basemap tiles, satellite its imagery), that layer falls back to the free map rather than staying blank.
  const [esriFailed, setEsriFailed] = useState<Partial<Record<MapLayerId, boolean>>>({});
  const tileStats = useRef<Record<string, { ok: number; bad: number }>>({});
  const esri = basemapsFor(maps);
  const layers = Object.fromEntries((Object.keys(esri) as MapLayerId[]).map((id) => [id, esriFailed[id] ? MAP_LAYERS[id] : esri[id]])) as Record<MapLayerId, (typeof MAP_LAYERS)[MapLayerId]>;
  const layer = layers[layerId];
  const onTile = (ok: boolean, part: 'base' | 'overlay' = 'base') => {
    if (layer.provider !== 'esri' || esriFailed[layer.id]) return;
    const k = `${layer.id}:${part}`;
    const t = (tileStats.current[k] ??= { ok: 0, bad: 0 });
    if (ok) t.ok++;
    else if (++t.bad >= 4 && t.ok === 0) setEsriFailed((f) => ({ ...f, [layer.id]: true }));
  };

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fit = useCallback(() => {
    if (!size.w || !size.h) return;
    const pts = markers.length ? markers : me ? [me] : [];
    const f = fitBounds(pts, size.w, size.h, { padding: 48, maxZoom: 17 });
    setView({ lat: f.center.lat, lng: f.center.lng, zoom: pts.length ? f.zoom : 8 });
  }, [markers, me, size.w, size.h]);

  const key = `${fitKey ?? ''}|${size.w > 0}`;
  useEffect(() => {
    if (size.w && fitted !== key) {
      if (!followRef.current) fit();
      setFitted(key);
    }
  }, [key, size.w, fit, fitted]);

  useEffect(() => {
    if (follow && me) setView((v) => (Math.abs(v.lat - me.lat) < 1e-7 && Math.abs(v.lng - me.lng) < 1e-7 ? v : { ...v, lat: me.lat, lng: me.lng }));
  }, [follow, me?.lat, me?.lng]);
  useEffect(() => {
    if (!followPref || !me || !size.w || autoFollowed.current) return;
    autoFollowed.current = true;
    setView((v) => ({ lat: me.lat, lng: me.lng, zoom: Math.max(v.zoom, 15) }));
    setFollow(true);
  }, [followPref, me?.lat, me?.lng, size.w]);
  useEffect(() => {
    if (!followSignal || !me) return;
    setView((v) => ({ lat: me.lat, lng: me.lng, zoom: Math.max(v.zoom, 17) }));
    setFollow(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followSignal]);
  const changeFollowPref = (on: boolean) => {
    setFollowPref(on);
    try {
      localStorage.setItem(FOLLOW_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
    if (on && me) {
      setView((v) => ({ lat: me.lat, lng: me.lng, zoom: Math.max(v.zoom, 15) }));
      setFollow(true);
    }
  };
  const recenter = () => {
    if (!me) return;
    if (follow) return setFollow(false);
    setView((v) => ({ lat: me.lat, lng: me.lng, zoom: Math.max(v.zoom, 15) }));
    setFollow(true);
  };

  const chooseLayer = (id: MapLayerId) => {
    setLayerId(id);
    try {
      localStorage.setItem(LAYER_KEY, id);
    } catch {
      /* ignore */
    }
  };

  // ─── Projection helpers ────────────────────────────────────────────────────
  const z = view.zoom;
  const c = lngLatToWorld(view.lng, view.lat, z);
  const originX = c.x - size.w / 2;
  const originY = c.y - size.h / 2;
  const toScreen = (lat: number, lng: number) => {
    const w = lngLatToWorld(lng, lat, z);
    return { x: w.x - originX, y: w.y - originY };
  };
  const toLatLng = (sx: number, sy: number) => worldToLngLat(originX + sx, originY + sy, z);

  const zoomAt = useCallback(
    (delta: number, sx?: number, sy?: number) => {
      setView((v) => {
        const nz = Math.max(MIN_Z, Math.min(MAX_Z, v.zoom + delta));
        if (nz === v.zoom) return v;
        const px = followRef.current ? size.w / 2 : (sx ?? size.w / 2);
        const py = followRef.current ? size.h / 2 : (sy ?? size.h / 2);
        const cw = lngLatToWorld(v.lng, v.lat, v.zoom);
        const anchor = worldToLngLat(cw.x - size.w / 2 + px, cw.y - size.h / 2 + py, v.zoom);
        // Keep the point under the cursor fixed.
        const aw = lngLatToWorld(anchor.lng, anchor.lat, nz);
        const nc = worldToLngLat(aw.x - px + size.w / 2, aw.y - py + size.h / 2, nz);
        return { lat: nc.lat, lng: nc.lng, zoom: nz };
      });
    },
    [size.w, size.h],
  );

  const panBy = useCallback((dx: number, dy: number) => {
    setView((v) => {
      const cw = lngLatToWorld(v.lng, v.lat, v.zoom);
      const n = worldToLngLat(cw.x + dx, cw.y + dy, v.zoom);
      return { lat: Math.max(-85, Math.min(85, n.lat)), lng: ((((n.lng + 180) % 360) + 360) % 360) - 180, zoom: v.zoom };
    });
  }, []);

  // ─── Pointer handling: drag to pan, pinch to zoom, tap to select ──────────
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ moved: number; pinchDist: number | null; target: HTMLElement | null }>({ moved: 0, pinchDist: null, target: null });

  const onPointerDown = (e: ReactPointerEvent) => {
    if ((e.target as HTMLElement).closest('.map-ui')) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    if (drawing) {
      const rect = box.current!.getBoundingClientRect();
      setTrace([{ x: e.clientX - rect.left, y: e.clientY - rect.top }]);
      return;
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) gesture.current = { moved: 0, pinchDist: null, target: e.target as HTMLElement };
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    if (drawing) {
      if (!trace) return;
      const rect = box.current!.getBoundingClientRect();
      const pt = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const last = trace[trace.length - 1]!;
      if (Math.hypot(pt.x - last.x, pt.y - last.y) >= 4) setTrace([...trace, pt]);
      return;
    }
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    const cur = { x: e.clientX, y: e.clientY };
    pointers.current.set(e.pointerId, cur);
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      const g = gesture.current;
      g.moved += 10;
      if (g.pinchDist == null) g.pinchDist = d;
      else if (d / g.pinchDist > 1.6 || d / g.pinchDist < 0.62) {
        const rect = box.current!.getBoundingClientRect();
        zoomAt(d > g.pinchDist ? 1 : -1, (a!.x + b!.x) / 2 - rect.left, (a!.y + b!.y) / 2 - rect.top);
        g.pinchDist = d;
      }
      return;
    }
    const dx = cur.x - prev.x;
    const dy = cur.y - prev.y;
    gesture.current.moved += Math.abs(dx) + Math.abs(dy);
    if (gesture.current.moved > 4) {
      if (followRef.current) setFollow(false);
      panBy(-dx, -dy);
    }
  };
  const onPointerUp = (e: ReactPointerEvent) => {
    if (drawing) {
      if (trace && trace.length >= 3) onDraw?.(trace.map((p) => toLatLng(p.x, p.y)));
      setTrace(null);
      return;
    }
    const wasTap = pointers.current.size === 1 && gesture.current.moved <= 4;
    pointers.current.delete(e.pointerId);
    if (!wasTap) return;
    // With pointer capture the up event targets the map, so use where the press started.
    if (gesture.current.target?.closest('.me-car')) {
      setCarPicker((v) => !v);
      return;
    }
    const markerEl = gesture.current.target?.closest<HTMLElement>('[data-marker]');
    if (markerEl && onMarkerClick) {
      onMarkerClick(markerEl.dataset.marker!);
      return;
    }
    if (onMapClick) {
      const rect = box.current!.getBoundingClientRect();
      let sx = e.clientX - rect.left;
      let sy = e.clientY - rect.top;
      if (turning) {
        // Undo the map's turn: screen point → the unturned map's point.
        const a = (-rot * Math.PI) / 180;
        const dx = sx - size.w / 2;
        const dy = sy - size.h / 2;
        sx = size.w / 2 + dx * Math.cos(a) - dy * Math.sin(a);
        sy = size.h / 2 + dx * Math.sin(a) + dy * Math.cos(a);
      }
      onMapClick(toLatLng(sx, sy));
    }
  };

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    let acc = 0;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      acc += e.deltaY;
      if (Math.abs(acc) < 60) return;
      const rect = el.getBoundingClientRect();
      zoomAt(acc < 0 ? 1 : -1, e.clientX - rect.left, e.clientY - rect.top);
      acc = 0;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const onKeyDown = (e: ReactKeyboardEvent) => {
    const step = 80;
    const k: Record<string, () => void> = {
      ArrowLeft: () => (setFollow(false), panBy(-step, 0)),
      ArrowRight: () => (setFollow(false), panBy(step, 0)),
      ArrowUp: () => (setFollow(false), panBy(0, -step)),
      ArrowDown: () => (setFollow(false), panBy(0, step)),
      '+': () => zoomAt(1),
      '=': () => zoomAt(1),
      '-': () => zoomAt(-1),
    };
    if (k[e.key] && e.target === e.currentTarget) {
      e.preventDefault();
      k[e.key]!();
    }
  };

  // The car turns the short way round (350° → 10° is +20°, not −340°).
  const lastAngle = useRef<number | null>(null);
  const h = me?.heading != null && Number.isFinite(me.heading) ? me.heading : null;
  if (h != null) {
    const prev = lastAngle.current;
    lastAngle.current = prev == null ? Math.round(h) : prev + ((((Math.round(h) - prev) % 360) + 540) % 360) - 180;
  }
  const carAngle = lastAngle.current;
  // Heading up: while following you, the map turns so the way you're driving is up and the car points straight
  // ahead. Dragging the map stops following and puts north back up.
  const rot = headingUp && follow && !!me && carAngle != null ? -carAngle : 0;
  const turning = rot !== 0;
  // When the map is turned, the corners need tiles from beyond the box.
  const pad = turning ? Math.ceil((Math.hypot(size.w, size.h) - Math.min(size.w, size.h)) / 2) : 0;
  // ─── Tiles (over-zoomed past the layer's native max) ──────────────────────
  const tileSet = (url: string, tileSize: number, maxZoom: number, prefix: string) => {
    if (!size.w || !size.h) return [];
    // 512-px tiles cover the area of a 256-px tile one zoom level up.
    const off = Math.log2(tileSize / TILE_SIZE);
    const tz = Math.max(0, Math.min(z - off, maxZoom - off));
    const ts = tileSize * 2 ** (z - off - tz);
    const n = 2 ** tz;
    const out: { key: string; src: string; left: number; top: number; size: number }[] = [];
    const x0 = Math.floor((originX - pad) / ts);
    const y0 = Math.floor((originY - pad) / ts);
    const x1 = Math.floor((originX + size.w + pad) / ts);
    const y1 = Math.floor((originY + size.h + pad) / ts);
    for (let ty = y0; ty <= y1; ty++) {
      if (ty < 0 || ty >= n) continue;
      for (let tx = x0; tx <= x1; tx++) {
        const wx = ((tx % n) + n) % n;
        out.push({ key: `${prefix}/${tz}/${tx}/${ty}`, src: tileUrl(url, tz, wx, ty), left: tx * ts - originX, top: ty * ts - originY, size: ts });
      }
    }
    return out;
  };
  const tiles = useMemo(() => tileSet(layer.url, layer.tileSize ?? TILE_SIZE, layer.maxZoom, `${layer.provider ?? 'free'}-${layer.id}`), [size.w, size.h, z, originX, originY, layer, pad]);
  const overlayTiles = useMemo(
    () => (layer.overlayUrl ? tileSet(layer.overlayUrl, layer.overlayTileSize ?? TILE_SIZE, layer.maxZoom, `ov-${layer.id}`) : []),
    [size.w, size.h, z, originX, originY, layer, pad],
  );

  // ─── House numbers (county address points) when zoomed in ─────────────────
  const [houseNums, setHouseNums] = useState<{ lat: number; lng: number; n: string }[]>([]);
  const showHouseNums = !!auth?.me && z >= HOUSE_NUMBER_ZOOM && size.w > 0;
  const bboxKey = showHouseNums
    ? (() => {
        const nw = worldToLngLat(originX - 64, originY - 64, z);
        const se = worldToLngLat(originX + size.w + 64, originY + size.h + 64, z);
        // Round so small pans reuse the same request.
        const r = (v: number) => Math.round(v * 400) / 400;
        return [r(nw.lng) - 0.0025, r(se.lat) - 0.0025, r(se.lng) + 0.0025, r(nw.lat) + 0.0025].map((v) => v.toFixed(5)).join(',');
      })()
    : '';
  useEffect(() => {
    if (!bboxKey) return;
    let alive = true;
    const t = setTimeout(() => {
      api
        .get<{ points: { lat: number; lng: number; n: string }[] }>(`/api/map/address-points?bbox=${bboxKey}`)
        .then((r) => alive && setHouseNums(r.points))
        .catch(() => undefined);
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [bboxKey]);

  const routePts = (route ?? []).map((p) => toScreen(p.lat, p.lng));
  const meS = me ? toScreen(me.lat, me.lng) : null;
  const metersPerPx = me?.accuracy ? (156543.03392 * Math.cos((me.lat * Math.PI) / 180)) / 2 ** z : 0;
  const ordered = [...markers].sort((a, b) => Number(!!a.selected) - Number(!!b.selected) || Number(!a.dot) - Number(!b.dot));

  return (
    <div
      ref={box}
      className={`slippy ${crosshair ? 'crosshair' : ''} ${drawing ? 'drawing' : ''}`}
      style={{ height }}
      role="application"
      aria-label={`${ariaLabel}. Arrow keys pan, plus and minus zoom.`}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={(e) => pointers.current.delete(e.pointerId)}
      onDoubleClick={(e) => {
        if ((e.target as HTMLElement).closest('.map-ui,[data-marker]')) return;
        const rect = box.current!.getBoundingClientRect();
        zoomAt(1, e.clientX - rect.left, e.clientY - rect.top);
      }}
    >
      <div className="slippy-rot" style={turning ? { transform: `rotate(${rot}deg)` } : undefined}>
      <div className="slippy-tiles" aria-hidden="true">
        {tiles.map((t) => (
          <img
            key={t.key}
            src={t.src}
            alt=""
            draggable={false}
            referrerPolicy="strict-origin-when-cross-origin"
            style={{ left: t.left, top: t.top, width: t.size, height: t.size }}
            onLoad={() => onTile(true)}
            onError={(e) => ((e.currentTarget as HTMLImageElement).style.visibility = 'hidden', onTile(false))}
          />
        ))}
        {overlayTiles.map((t) => (
          <img
            key={t.key}
            src={t.src}
            alt=""
            draggable={false}
            referrerPolicy="strict-origin-when-cross-origin"
            style={{ left: t.left, top: t.top, width: t.size, height: t.size }}
            onLoad={() => onTile(true, 'overlay')}
            onError={(e) => ((e.currentTarget as HTMLImageElement).style.visibility = 'hidden', onTile(false, 'overlay'))}
          />
        ))}
      </div>
      {showHouseNums && houseNums.length > 0 && (
        <div className="slippy-hn" aria-hidden="true">
          {houseNums.map((h, i) => {
            const p = toScreen(h.lat, h.lng);
            if (p.x < -20 || p.y < -20 || p.x > size.w + 20 || p.y > size.h + 20) return null;
            return (
              <span key={i} className="hn" style={{ left: p.x, top: p.y, ...(turning ? { transform: `translate(-50%, 3px) rotate(${-rot}deg)` } : {}) }}>
                {h.n}
              </span>
            );
          })}
        </div>
      )}
      {routePts.length > 1 && (
        <svg className="slippy-overlay" width={size.w} height={size.h} aria-hidden="true">
          <polyline points={routePts.map((p) => `${p.x},${p.y}`).join(' ')} className="route-line-bg" />
          <polyline points={routePts.map((p) => `${p.x},${p.y}`).join(' ')} className="route-line" />
        </svg>
      )}
      {(vectors?.length ?? 0) > 0 && (
        <svg className="slippy-overlay" width={size.w} height={size.h} aria-hidden="true">
          {vectors!.map((v, i) => {
            const pts = v.points.map((p) => toScreen(p.lat, p.lng)).map((p) => `${p.x},${p.y}`).join(' ');
            return v.area ? (
              <polygon key={i} points={pts} fill={v.color} fillOpacity={0.12} stroke={v.color} strokeWidth={2} />
            ) : (
              <polyline key={i} points={pts} fill="none" stroke={v.color} strokeWidth={3} strokeLinejoin="round" />
            );
          })}
        </svg>
      )}
      {((shapes?.length ?? 0) > 0 || trace) && (
        <svg className="slippy-overlay" width={size.w} height={size.h} aria-hidden="true">
          {(shapes ?? []).map((sh, i) => (
            <polygon key={i} className="lasso-shape" points={sh.map((p) => toScreen(p.lat, p.lng)).map((p) => `${p.x},${p.y}`).join(' ')} />
          ))}
          {trace && <polyline className="lasso-trace" points={trace.map((p) => `${p.x},${p.y}`).join(' ')} />}
        </svg>
      )}
      {meS && (
        <button
          type="button"
          className="me-car"
          style={{ left: meS.x, top: meS.y }}
          title="Your location — tap to change your vehicle"
          aria-label="Your location. Change your vehicle on the map"
          onClick={(e) => {
            // Keyboard activation (taps are handled in onPointerUp, so dragging from the car still pans).
            if (e.detail === 0) setCarPicker((v) => !v);
          }}
        >
          {metersPerPx > 0 && <span className="me-acc" style={{ width: (me!.accuracy! / metersPerPx) * 2, height: (me!.accuracy! / metersPerPx) * 2 }} />}
          <CarShape id={carIcon} style={carAngle != null ? { transform: `rotate(${carAngle}deg)` } : undefined} />
        </button>
      )}
      {ordered.some((m) => m.diamond) && (
        <div className="slippy-diamonds" aria-hidden="true">
          {ordered.filter((m) => m.diamond).map((m) => {
            const s = toScreen(m.lat, m.lng);
            if (s.x < -40 - pad || s.y < -40 - pad || s.x > size.w + 40 + pad || s.y > size.h + 40 + pad) return null;
            return <span key={`d-${m.id}`} className="paper-diamond" style={{ left: s.x, top: s.y }} />;
          })}
        </div>
      )}
      {ordered.map((m) => {
        const s = toScreen(m.lat, m.lng);
        if (s.x < -40 - pad || s.y < -40 - pad || s.x > size.w + 40 + pad || s.y > size.h + 40 + pad) return null;
        return (
          <button
            key={m.id}
            type="button"
            data-marker={m.id}
            className={`pin ${m.dot ? 'dot-pin' : ''} ${m.selected ? 'selected' : ''}`}
            style={{ left: s.x, top: s.y, ['--pin' as string]: m.color, ...(turning && !m.dot ? { transform: `translate(-50%, -100%)${m.selected ? ' scale(1.3)' : ''} rotate(${-rot}deg)`, transformOrigin: '50% 100%' } : {}) }}
            title={m.title}
            aria-label={m.title}
            aria-pressed={m.selected ?? false}
            onClick={(e) => {
              // Keyboard activation (pointer taps are handled in onPointerUp).
              if (e.detail === 0) onMarkerClick?.(m.id);
            }}
          >
            {!m.dot && (
              <svg viewBox="0 0 24 34" width="26" height="36" aria-hidden="true">
                <path d="M12 33C12 33 22 20.5 22 12A10 10 0 0 0 2 12C2 20.5 12 33 12 33Z" fill="var(--pin)" stroke="#fff" strokeWidth="2" />
                {m.label ? (
                  <text x="12" y="16" textAnchor="middle" fontSize={m.label.length > 2 ? 8 : 10} fontWeight="700" fill="#fff">
                    {m.label}
                  </text>
                ) : (
                  <circle cx="12" cy="12" r="3.5" fill="#fff" />
                )}
              </svg>
            )}
          </button>
        );
      })}

      </div>

      {carPicker && me && (
        <div className="map-ui map-carpick" role="dialog" aria-label="Your vehicle on the map">
          <div className="map-carpick-t">Your vehicle on the map</div>
          <div className="map-carpick-row">
            {CAR_CHOICES.map((c) => (
              <button key={c.id} type="button" className={(carIcon ?? 'patrol') === c.id ? 'on' : ''} aria-pressed={(carIcon ?? 'patrol') === c.id} onClick={() => chooseCar(c.id)}>
                <CarShape id={c.id} />
                <span>{c.label}</span>
              </button>
            ))}
          </div>
          <button type="button" className="btn link small" onClick={() => setCarPicker(false)}>
            Close
          </button>
        </div>
      )}
      <div className="map-ui map-layers" role="group" aria-label="Map style">
        {(Object.keys(layers) as MapLayerId[]).map((id) => (
          <button key={id} type="button" className={layerId === id ? 'active' : ''} aria-pressed={layerId === id} onClick={() => chooseLayer(id)}>
            {layers[id].label}
          </button>
        ))}
      </div>
      <div className="map-ui map-zoom">
        <button type="button" aria-label="Zoom in" onClick={() => zoomAt(1)}>
          +
        </button>
        <input
          className="map-zoom-slider"
          type="range"
          min={MIN_Z}
          max={MAX_Z}
          step={1}
          value={z}
          aria-label="Zoom"
          aria-orientation="vertical"
          onChange={(e) => zoomAt(Number(e.target.value) - z)}
        />
        <button type="button" aria-label="Zoom out" onClick={() => zoomAt(-1)}>
          −
        </button>
        {me && (
          <button type="button" className={`map-follow ${follow ? 'on' : ''}`} aria-label={follow ? 'Stop following me' : 'Center on me'} aria-pressed={follow} title={follow ? 'Following you — tap to stop' : 'Center on me and follow'} onClick={recenter}>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
              <circle cx="12" cy="12" r="2.6" fill="currentColor" />
              <path d="M12 1.5v4M12 18.5v4M1.5 12h4M18.5 12h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        )}
        {me && (
          <button type="button" className="map-compass" aria-label={headingUp ? 'Heading up: switch to north up' : 'North up: switch to heading up'} aria-pressed={headingUp} title={headingUp ? 'Heading up (map turns with you) — tap for north up' : 'North up — tap for heading up'} onClick={toggleHeadingUp}>
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" style={{ transform: `rotate(${rot}deg)` }}>
              <path d="M12 3 8 12h8Z" fill="#dc2626" />
              <path d="M12 21 8 12h8Z" fill="currentColor" opacity="0.45" />
            </svg>
            <span className="map-compass-l">{headingUp ? '' : 'N'}</span>
          </button>
        )}
        <button type="button" aria-label="Fit all pins" title="Fit all pins" onClick={() => (setFollow(false), fit())}>
          ⤢
        </button>
      </div>
      {me && (
        <label className="map-ui map-followpref">
          <input type="checkbox" checked={followPref} onChange={(e) => changeFollowPref(e.target.checked)} /> Always follow me
        </label>
      )}
      {children && <div className="map-ui map-extra">{children}</div>}
      <div className="map-ui map-attrib">
        {esriFailed[layerId] && <span className="map-warn">Esri {layer.label.toLowerCase()} map didn’t load — showing the free one. </span>}
        {layer.provider === 'esri' && <span className="esri-mark">Powered by Esri · </span>}
        {layer.attribution}
      </div>
    </div>
  );
}

export function PinLegend({ items }: { items: { color: string; label: string; count?: number }[] }) {
  return (
    <ul className="pin-legend" aria-label="Pin legend">
      {items.map((i) => (
        <li key={i.label}>
          <span className="legend-dot" style={{ background: i.color }} />
          {i.label}
          {i.count != null && <span className="muted"> ({i.count})</span>}
        </li>
      ))}
    </ul>
  );
}
